import React, { useState, useEffect, useRef } from 'react';
import { Typography, Grid, IconButton, Tooltip, Snackbar, Tabs, Tab, Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CodeIcon from '@mui/icons-material/Code';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import SyncIcon from '@mui/icons-material/Sync';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import { CSVLink } from 'react-csv';
import { Icon } from '@mui/material';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import TableOmniWithSkeleton from 'allGlobalComponents/TableOmniWithSkeleton';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import RichTextEditor from '../modals/RichTextEditor';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import { globalDateFormat } from 'app/constants';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';

const useStyles = makeStyles(theme => ({
	headerRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		marginBottom: theme.spacing(3),
		flexWrap: 'wrap',
		gap: theme.spacing(2),
		width: '100%',
		boxSizing: 'border-box',
		[theme.breakpoints.down('xs')]: {
			flexDirection: 'column',
			alignItems: 'stretch'
		}
	},
	leftControls: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		flexWrap: 'wrap',
		[theme.breakpoints.down('xs')]: {
			width: '100%'
		}
	},
	rightControls: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		flexWrap: 'wrap',
		[theme.breakpoints.down('xs')]: {
			width: '100%',
			justifyContent: 'flex-end'
		}
	},
	tableSection: {
		backgroundColor: theme.palette.background.paper,
		borderRadius: 12,
		overflow: 'hidden',
		width: '100%',
		boxSizing: 'border-box'
	},
	actionButtons: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	actionButton: {
		padding: 6,
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	snoCell: {
		fontWeight: 500,
		color: theme.palette.text.secondary
	},
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		padding: theme.spacing(2)
	},
	fieldRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5)
	},
	fieldLabel: {
		fontSize: '1.2rem',
		color: theme.palette.text.secondary,
		fontWeight: 500
	},
	characterCount: {
		fontSize: '1.2rem',
		color: theme.palette.type === 'dark' ? '#9CA3AF' : theme.palette.text.secondary,
		textAlign: 'right',
		marginTop: theme.spacing(0.5)
	},
	characterCountWarning: {
		color: theme.palette.warning.main
	},
	characterCountError: {
		color: theme.palette.error.main
	},
	viewDialogContent: {
		padding: theme.spacing(2)
	},
	viewSection: {
		marginBottom: theme.spacing(2)
	},
	viewHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(0.5)
	},
	viewLabel: {
		fontSize: 12,
		fontWeight: 600,
		color: theme.palette.type === 'dark' ? '#9CA3AF' : theme.palette.text.secondary,
	},
	viewValue: {
		fontSize: 14,
		color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
		padding: theme.spacing(1.5),
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
		borderRadius: 8,
		wordBreak: 'break-word',
		whiteSpace: 'pre-wrap'
	},
	htmlPreview: {
		padding: theme.spacing(2),
		backgroundColor: theme.palette.type === 'dark' ? '#111827' : '#FFFFFF',
		borderRadius: 8,
		border: `1px solid ${theme.palette.divider}`,
		maxHeight: 300,
		overflow: 'auto',
		color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
		'& img': {
			maxWidth: '100%'
		},
		'& p, & span, & div, & td, & th': {
			color: theme.palette.type === 'dark' ? '#E5E7EB' : 'inherit'
		},
		'& a': {
			color: theme.palette.type === 'dark' ? '#60A5FA' : theme.palette.primary.main,
			cursor: 'pointer',
			textDecoration: 'underline'
		},
		'& h1, & h2, & h3, & h4, & h5, & h6': {
			color: theme.palette.type === 'dark' ? '#FFFFFF' : 'inherit'
		},
		'& ul': {
			listStyleType: 'disc',
			paddingLeft: 40,
			marginLeft: 0
		},
		'& ol': {
			listStyleType: 'decimal',
			paddingLeft: 40,
			marginLeft: 0
		},
		'& li': {
			display: 'list-item',
			color: theme.palette.type === 'dark' ? '#E5E7EB' : 'inherit',
			marginBottom: theme.spacing(0.5)
		}
	},
	copyIconButton: {
		padding: 6,
		color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
			color: theme.palette.primary.main
		}
	},
	copyIconButtonSuccess: {
		color: '#22C55E',
		'&:hover': {
			color: '#22C55E'
		}
	},
	editorSection: {
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'}`,
		borderRadius: 4,
		overflow: 'hidden',
		marginTop: theme.spacing(1)
	},
	tabsContainer: {
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6'
	},
	tab: {
		minHeight: 40,
		textTransform: 'none',
		fontSize: 13,
		fontWeight: 500,
		color: theme.palette.text.secondary,
		borderBottom: '2px solid transparent',
		'&.Mui-selected': {
			color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.08)',
			borderBottom: `2px solid ${theme.palette.type === 'dark' ? '#60A5FA' : theme.palette.primary.main}`
		},
		'& .MuiTab-wrapper': {
			flexDirection: 'row',
			gap: theme.spacing(0.5)
		}
	},
	tabIcon: {
		fontSize: 18,
		marginRight: theme.spacing(0.5)
	},
	previewContainer: {
		padding: theme.spacing(2),
		minHeight: 200,
		maxHeight: 400,
		overflow: 'auto',
		backgroundColor: theme.palette.background.paper,
		border: 'none',
		borderRadius: 0
	},
	previewContent: {
		color: theme.palette.text.primary,
		fontSize: 14,
		lineHeight: 1.6,
		'& img': {
			maxWidth: '100%',
			height: 'auto'
		},
		'& a': {
			color: theme.palette.primary.main,
			cursor: 'pointer',
			textDecoration: 'underline'
		},
		'& ul': {
			listStyleType: 'disc',
			paddingLeft: 40,
			marginLeft: 0
		},
		'& ol': {
			listStyleType: 'decimal',
			paddingLeft: 40,
			marginLeft: 0
		},
		'& li': {
			display: 'list-item',
			marginBottom: theme.spacing(0.5)
		},
		'& blockquote': {
			borderLeft: `3px solid ${theme.palette.divider}`,
			paddingLeft: theme.spacing(2),
			marginLeft: 0,
			color: theme.palette.text.secondary
		}
	},
	htmlCodeContainer: {
		paddingTop: theme.spacing(7),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		minHeight: 200,
		maxHeight: 400,
		overflow: 'auto',
		backgroundColor: theme.palette.background.paper,
		position: 'relative'
	},
	htmlCodeHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		zIndex: 10,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1, 2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	copyButton: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5),
		fontSize: 12,
		fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#E5E7EB' : '#374151',
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#E5E7EB',
		border: 'none',
		borderRadius: 6,
		cursor: 'pointer',
		marginTop: theme.spacing(1),
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : '#D1D5DB'
		}
	},
	copyButtonSuccess: {
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
		color: '#22C55E'
	},
	pasteButton: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5),
		fontSize: 12,
		fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#93C5FD' : '#2563EB',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
		border: 'none',
		borderRadius: 6,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)'
		}
	},
	clearButton: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5),
		fontSize: 12,
		fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#F87171' : '#DC2626',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
		border: 'none',
		borderRadius: 6,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.15)'
		}
	},
	htmlCode: {
		fontFamily: 'monospace',
		fontSize: 12,
		color: theme.palette.type === 'dark' ? '#E6EDF3' : '#24292F',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		margin: 0
	},
	emptyPreview: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 200,
		color: theme.palette.text.secondary,
		fontStyle: 'italic',
		fontSize: 14,
		backgroundColor: theme.palette.background.paper
	},
	htmlButtonsRow: {
		display: 'flex',
		gap: theme.spacing(1)
	},
	nameErrorText: {
		color: theme.palette.error.main,
		fontSize: '1rem',
		fontWeight: 600,
		marginTop: theme.spacing(1),
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	inputError: {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				borderColor: theme.palette.error.main
			},
			'&:hover fieldset': {
				borderColor: theme.palette.error.main
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.error.main
			}
		}
	},
	refreshButton: {
		padding: 8,
		color: theme.palette.type === 'dark' ? '#FFFFFF' : '#000000',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		},
		'& .MuiSvgIcon-root': {
			fontSize: 24,
			fontWeight: 'bold'
		}
	},
}));

function TemplatesTab({ userData = null }) {
	const classes = useStyles();
	const theme = useTheme();
	const csvLinkRef = useRef(null);

	// Data states
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [totalItems, setTotalItems] = useState(0);

	// Date range state
	const [dateRange, setDateRange] = useState({
		startDate: moment().startOf('day'),
		endDate: moment().endOf('day')
	});

	// Pagination state
	const [currentParams, setCurrentParams] = useState({
		page: 0,
		limit: 10
	});

	// Modal states
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	// Form state
	const [formData, setFormData] = useState({
		name: '',
		body: ''
	});
	const [nameLength, setNameLength] = useState(0);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [viewingTemplate, setViewingTemplate] = useState(null);
	const [deletingTemplate, setDeletingTemplate] = useState(null);

	// Name validation state
	const [nameError, setNameError] = useState('');

	// Editor tab state
	const [editorTab, setEditorTab] = useState(0);
	const [htmlCopied, setHtmlCopied] = useState(false);

	// View dialog copy states
	const [nameCopied, setNameCopied] = useState(false);
	const [textCopied, setTextCopied] = useState(false);
	const [viewHtmlCopied, setViewHtmlCopied] = useState(false);

	// Loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Snackbar state
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		severity: 'success'
	});

	// Export state
	const [exportData, setExportData] = useState([]);

	// Fetch templates
	const fetchTemplates = () => {
		setLoading(true);
		const apiPayload = {
			page: currentParams.page,
			limit: currentParams.limit,
		};

		if (userData?.id) {
			apiPayload.clientId = userData?.id;
		}
		CoreHttpHandler.request(
			'emailCampaign',
			'emailTemplateList',
			apiPayload,
			response => {
				const templates = response.data?.data?.data?.emailTemplates || [];
				const total = response.data?.data?.data?.totalItems || 0;
				setData(templates);
				setTotalItems(total);
				setLoading(false);
			},
			error => {
				console.error('Error fetching templates:', error);
				setData([]);
				setTotalItems(0);
				setLoading(false);
				showSnackbar('Failed to fetch templates', 'error');
			}
		);
	};

	// Initial fetch
	useEffect(() => {
		fetchTemplates();
	}, [currentParams, dateRange]);

	// Show snackbar helper
	const showSnackbar = (message, severity = 'success') => {
		setSnackbar({ open: true, message, severity });
	};

	// Handle snackbar close
	const handleSnackbarClose = () => {
		setSnackbar(prev => ({ ...prev, open: false }));
	};

	// Copy functions for view dialog
	const copyName = () => {
		if (viewingTemplate?.email_template_name) {
			navigator.clipboard.writeText(viewingTemplate.email_template_name).then(() => {
				setNameCopied(true);
				setTimeout(() => setNameCopied(false), 2000);
			}).catch(err => {
				console.error('Failed to copy:', err);
				showSnackbar('Failed to copy', 'error');
			});
		}
	};

	const copyText = () => {
		if (viewingTemplate?.email_template_body) {
			navigator.clipboard.writeText(viewingTemplate.email_template_body).then(() => {
				setTextCopied(true);
				setTimeout(() => setTextCopied(false), 2000);
			}).catch(err => {
				console.error('Failed to copy:', err);
				showSnackbar('Failed to copy', 'error');
			});
		}
	};

	const copyViewHtml = () => {
		if (viewingTemplate?.email_template_html) {
			navigator.clipboard.writeText(viewingTemplate.email_template_html).then(() => {
				setViewHtmlCopied(true);
				setTimeout(() => setViewHtmlCopied(false), 2000);
			}).catch(err => {
				console.error('Failed to copy:', err);
				showSnackbar('Failed to copy', 'error');
			});
		}
	};

	// Handle date range change
	const handleDateRangeChange = (range) => {
		setDateRange({
			startDate: moment(range.startDate).startOf('day'),
			endDate: moment(range.endDate).endOf('day')
		});
		setCurrentParams(prev => ({ ...prev, page: 0 }));
	};

	// Handle pagination change
	const handlePaginationChange = (page, limit) => {
		setCurrentParams({ page, limit });
	};

	// Handle refresh
	const handleRefresh = () => {
		fetchTemplates();
	};

	// Generate complete HTML
	const generateCompleteHTML = (bodyContent) => {
		return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Email Template</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333333;
            margin: 0;
            padding: 20px;
        }
        img { max-width: 100%; height: auto; }
        a { color: #1a73e8; }
    </style>
</head>
<body>
    ${bodyContent || ''}
</body>
</html>`;
	};

	// Strip HTML tags
	const stripHtmlTags = (html) => {
		const tmp = document.createElement('div');
		tmp.innerHTML = html;
		return tmp.textContent || tmp.innerText || '';
	};

	// Format HTML for display
	const formatHtml = (html) => {
		if (!html) return '';
		let formatted = html
			.replace(/></g, '>\n<')
			.replace(/(<[^/][^>]*>)/g, '\n$1')
			.replace(/(<\/[^>]+>)/g, '$1\n')
			.replace(/\n\s*\n/g, '\n')
			.trim();
		return formatted;
	};

	// Copy HTML to clipboard
	const copyHtmlToClipboard = () => {
		const htmlContent = generateCompleteHTML(formData.body);
		navigator.clipboard.writeText(formatHtml(htmlContent)).then(() => {
			setHtmlCopied(true);
			setTimeout(() => setHtmlCopied(false), 2000);
		}).catch(err => {
			console.error('Failed to copy:', err);
		});
	};

	// Paste HTML from clipboard
	const pasteHtmlFromClipboard = async () => {
		try {
			const clipboardText = await navigator.clipboard.readText();
			if (clipboardText) {
				let bodyContent = clipboardText;
				const bodyMatch = clipboardText.match(/<body[^>]*>([\s\S]*)<\/body>/i);
				if (bodyMatch) {
					bodyContent = bodyMatch[1];
				}
				setFormData(prev => ({ ...prev, body: bodyContent }));
			}
		} catch (err) {
			console.error('Failed to paste:', err);
			showSnackbar('Failed to paste from clipboard', 'error');
		}
	};

	// Clear HTML content
	const clearHtmlContent = () => {
		setFormData(prev => ({ ...prev, body: '' }));
	};

	// Upload file to server
	const uploadFileToServer = (file) => {
		return new Promise((resolve, reject) => {
			const form = new FormData();
			form.append('file', file, `${Date.now()}_${file.name.replace(/\s/g, '')}`);
			CoreHttpHandler.request(
				'content',
				'upload',
				{ params: form },
				res => resolve(res.data.data.link),
				error => reject(error)
			);
		});
	};

	// Handle image upload for editor
	const handleImageUpload = async (file) => {
		try {
			const uploadedUrl = await uploadFileToServer(file);
			return uploadedUrl;
		} catch (error) {
			console.error('Failed to upload image:', error);
			return URL.createObjectURL(file);
		}
	};

	// Handle editor tab change
	const handleEditorTabChange = (event, newValue) => {
		setEditorTab(newValue);
	};

	// Check if template name already exists
	const checkNameExists = (name, excludeId = null) => {
		if (!name.trim()) return false;
		const normalizedName = name.trim().toLowerCase();
		return data.some(template => {
			if (excludeId && template.id === excludeId) return false;
			return template.email_template_name?.toLowerCase() === normalizedName;
		});
	};

	// Handle create
	const handleCreate = () => {
		setFormData({ name: '', body: '' });
		setNameLength(0);
		setNameError('');
		setEditorTab(0);
		setCreateModalOpen(true);
	};

	// Handle create confirm
	const handleCreateConfirm = () => {
		if (!formData.name.trim()) {
			showSnackbar('Please enter template name', 'error');
			return;
		}

		if (checkNameExists(formData.name)) {
			setNameError(`Email template name "${formData.name.trim()}" already exists. Please choose a different name.`);
			return;
		}

		setIsCreating(true);
		const apiPayload = {
			email_template_name: formData.name.trim(),
			email_template_body: stripHtmlTags(formData.body),
			email_template_html: generateCompleteHTML(formData.body)
		};

		if (userData?.id) {
			apiPayload.clientId = userData?.id;
		}
		CoreHttpHandler.request(
			'emailCampaign',
			'emailTemplateAdd',
			apiPayload,
			response => {
				setIsCreating(false);
				if (response.data?.code === 200) {
					showSnackbar('Template created successfully', 'success');
					setCreateModalOpen(false);
					fetchTemplates();
				} else if (response.data?.code === 409) {
					setNameError(response.data?.message);
				}
			},
			error => {
				setIsCreating(false);
				const errorMessage = error.response?.data?.message || 'Failed to create template';
				if (errorMessage.toLowerCase().includes('exists') || errorMessage.toLowerCase().includes('duplicate')) {
					setNameError(errorMessage);
				} else {
					showSnackbar(errorMessage, 'error');
				}
			}
		);
	};

	// Handle view click
	const handleViewClick = (template) => {
		setViewingTemplate(template);
		setNameCopied(false);
		setTextCopied(false);
		setViewHtmlCopied(false);
		setViewModalOpen(true);
	};

	// Handle close view dialog
	const handleCloseViewDialog = () => {
		setViewModalOpen(false);
		setViewingTemplate(null);
		setNameCopied(false);
		setTextCopied(false);
		setViewHtmlCopied(false);
	};

	// Handle edit click
	const handleEditClick = (template) => {
		setEditingTemplate(template);
		let body = template.email_template_html || '';
		const bodyMatch = body.match(/<body[^>]*>([\s\S]*)<\/body>/i);
		if (bodyMatch) {
			body = bodyMatch[1];
		}
		setFormData({
			name: template.email_template_name || '',
			body: body
		});
		setNameLength(template.email_template_name?.length || 0);
		setNameError('');
		setEditorTab(0);
		setEditModalOpen(true);
	};

	// Handle edit confirm
	const handleEditConfirm = () => {
		if (!formData.name.trim()) {
			showSnackbar('Please enter template name', 'error');
			return;
		}

		if (checkNameExists(formData.name, editingTemplate?.id)) {
			setNameError(`Email template name "${formData.name.trim()}" already exists. Please choose a different name.`);
			return;
		}

		setIsUpdating(true);
		const apiPayload = {
			id: editingTemplate.id,
			email_template_name: formData.name.trim(),
			email_template_body: stripHtmlTags(formData.body),
			email_template_html: generateCompleteHTML(formData.body)
		};

		if (userData?.id) {
			apiPayload.clientId = userData?.id;
		}

		CoreHttpHandler.request(
			'emailCampaign',
			'emailTemplateUpdate',
			apiPayload,
			response => {
				setIsUpdating(false);
				if (response.data?.code === 200) {
					showSnackbar('Template updated successfully', 'success');
					setEditModalOpen(false);
					setEditingTemplate(null);
					fetchTemplates();
				} else if (response.data?.code != 200) {
					setNameError(response.data?.message);
				}
			},
			error => {
				setIsUpdating(false);
				const errorMessage = error.response?.data?.message || 'Failed to update template';
				if (errorMessage.toLowerCase().includes('exists') || errorMessage.toLowerCase().includes('duplicate')) {
					setNameError(errorMessage);
				} else {
					showSnackbar(errorMessage, 'error');
				}
			}
		);
	};

	// Handle delete click
	const handleDeleteClick = (template) => {
		setDeletingTemplate(template);
		setDeleteDialogOpen(true);
	};

	// Handle delete confirm
	const handleDeleteConfirm = () => {
		setIsDeleting(true);
		const apiPayload = { id: deletingTemplate.id };
		if (userData?.id) {
			apiPayload.clientId = userData?.id;
		}
		CoreHttpHandler.request(
			'emailCampaign',
			'emailTemplateDelete',
			apiPayload,
			response => {
				setIsDeleting(false);
				if (response.data?.code === 200) {
					showSnackbar('Template deleted successfully', 'success');
					setDeleteDialogOpen(false);
					setDeletingTemplate(null);
					fetchTemplates();
				}
			},
			error => {
				setIsDeleting(false);
				showSnackbar('Failed to delete template', 'error');
			}
		);
	};

	// Handle name change with validation
	const handleNameChange = (e) => {
		let value = e.target.value;
		value = value.replace(/\s/g, '_');

		if (value.length <= 100) {
			setFormData(prev => ({ ...prev, name: value }));
			setNameLength(value.length);

			if (value.trim()) {
				const excludeId = editModalOpen ? editingTemplate?.id : null;
				if (checkNameExists(value, excludeId)) {
					setNameError(`Email template name "${value.trim()}" already exists. Please choose a different name.`);
				} else {
					setNameError('');
				}
			} else {
				setNameError('');
			}
		}
	};

	// Handle body change
	const handleBodyChange = (html) => {
		setFormData(prev => ({ ...prev, body: html }));
	};

	const highlightParams = (html) => {
		if (!html) return html;
		// Replace {{1}}, {{2}}, etc with highlighted spans
		return html.replace(/\{\{(\d+)\}\}/g, '<span style="background-color: #A3E635; padding: 2px 6px; border-radius: 4px; font-weight: 500; color: #365A0E;">{{$1}}</span>');
	};

	// Table head cells
	const headCells = [
		{ id: 'id', numeric: false, disablePadding: false, label: 'ID' },
		{
			id: 'email_template_name',
			numeric: false,
			disablePadding: false,
			label: 'Template Name',
			msg: true,
			truncateLength: 40
		},
		{
			id: 'email_template_body',
			numeric: false,
			disablePadding: false,
			label: 'Body Preview',
			msg: true,
			truncateLength: 40
		},
		{
			id: 'view',
			numeric: false,
			disablePadding: false,
			label: 'View Template',
		},
		{
			id: 'dt',
			numeric: false,
			disablePadding: false,
			label: 'Date',
			dt: true
		},
		{
			id: 'edit',
			numeric: false,
			disablePadding: false,
			label: 'Edit',
		},
		{
			id: 'delete',
			numeric: false,
			disablePadding: false,
			label: 'Delete',
		}
	];

	// Modal content for create/edit
	const renderFormContent = () => (
		<div className={classes.formContainer}>
			<div className={classes.fieldRow}>
				<Typography className={classes.fieldLabel}>Template Name *</Typography>
				<FormFieldOmni
					variant="outlined"
					value={formData.name}
					onChange={handleNameChange}
					size="small"
					fullWidth
					placeholder="Enter template name (spaces will be replaced with _)"
					inputProps={{ maxLength: 100 }}
					error={!!nameError}
					className={nameError ? classes.inputError : ''}
				/>
				{nameError && (
					<Typography className={classes.nameErrorText}>
						{nameError}
					</Typography>
				)}
				{!nameError && (
					<Typography
						className={`${classes.characterCount} ${nameLength >= 100
							? classes.characterCountError
							: nameLength >= 80
								? classes.characterCountWarning
								: ''
							}`}
					>
						{nameLength}/100 characters
					</Typography>
				)}
			</div>

			<div className={classes.fieldRow}>
				<Typography className={classes.fieldLabel}>
					Template Body
					<span style={{ fontWeight: 400, marginLeft: 8, fontSize: 12, color: '#6B7280' }}>
						(Use param button or type {'{{1}}'}, {'{{2}}'}, etc. for personalization)
					</span>
				</Typography>
				<div className={classes.editorSection}>
					<div className={classes.tabsContainer}>
						<Tabs
							value={editorTab}
							onChange={handleEditorTabChange}
							indicatorColor="primary"
							textColor="primary"
							TabIndicatorProps={{ style: { display: 'none' } }}
						>
							<Tab
								className={classes.tab}
								label={
									<Box display="flex" alignItems="center">
										<EditIcon className={classes.tabIcon} />
										Compose
									</Box>
								}
							/>
							<Tab
								className={classes.tab}
								label={
									<Box display="flex" alignItems="center">
										<VisibilityIcon className={classes.tabIcon} />
										Preview
									</Box>
								}
							/>
							<Tab
								className={classes.tab}
								label={
									<Box display="flex" alignItems="center">
										<CodeIcon className={classes.tabIcon} />
										HTML
									</Box>
								}
							/>
						</Tabs>
					</div>

					{editorTab === 0 && (
						<RichTextEditor
							value={formData.body}
							onChange={handleBodyChange}
							minHeight={250}
							maxHeight={400}
							showCharacterCount={true}
							showParamButton={true}
							onImageUpload={handleImageUpload}
							toolbarOptions={[
								'undo', 'redo', '|',
								'heading', '|',
								'fontFamily', 'fontSize', 'fontSizeControls', '|',
								'bold', 'italic', 'underline', 'strikethrough', '|',
								'textColor', 'bgColor', '|',
								'bulletList', 'numberedList', '|',
								'alignLeft', 'alignCenter', 'alignRight', '|',
								'link', 'image', '|',
								'blockquote', 'code', '|',
								'clearFormat', '|',
								'param'
							]} />
					)}

					{editorTab === 1 && (
						<div className={classes.previewContainer}>
							{formData.body ? (
								<div
									className={classes.previewContent}
									dangerouslySetInnerHTML={{ __html: highlightParams(formData.body) }}
									onClick={(e) => {
										if (e.target.tagName === 'A' && e.target.href) {
											e.preventDefault();
											window.open(e.target.href, '_blank', 'noopener,noreferrer');
										}
									}}
								/>
							) : (
								<div className={classes.emptyPreview}>
									No content to preview. Start typing in the Compose tab.
								</div>
							)}
						</div>
					)}

					{editorTab === 2 && (
						<div className={classes.htmlCodeContainer}>
							<div className={classes.htmlCodeHeader}>
								<button
									className={classes.pasteButton}
									onClick={pasteHtmlFromClipboard}
								>
									<ContentPasteIcon style={{ fontSize: 14 }} />
									Paste HTML
								</button>
								<div className={classes.htmlButtonsRow}>
									{formData.body && (
										<button
											className={classes.clearButton}
											onClick={clearHtmlContent}
										>
											<ClearIcon style={{ fontSize: 14 }} />
											Clear
										</button>
									)}
									<button
										className={`${classes.copyButton} ${htmlCopied ? classes.copyButtonSuccess : ''}`}
										onClick={copyHtmlToClipboard}
										style={{ marginTop: 0 }}
									>
										{htmlCopied ? (
											<>
												<CheckIcon style={{ fontSize: 14 }} />
												Copied!
											</>
										) : (
											<>
												<ContentCopyIcon style={{ fontSize: 14 }} />
												Copy HTML
											</>
										)}
									</button>
								</div>
							</div>
							{formData.body ? (
								<pre className={classes.htmlCode}>
									{formatHtml(generateCompleteHTML(formData.body))}
								</pre>
							) : (
								<div className={classes.emptyPreview}>
									No HTML content. Start typing in the Compose tab or paste HTML.
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);

	// View modal content - Updated to use icon-only buttons with tooltips like ScheduleTab
	const renderViewContent = () => (
		<div className={classes.viewDialogContent}>
			<div className={classes.viewSection}>
				<div className={classes.viewHeader}>
					<Typography className={classes.viewLabel}>Template Name</Typography>
					<Tooltip title={nameCopied ? "Copied!" : "Copy"} arrow>
						<IconButton
							size="small"
							onClick={copyName}
							className={`${classes.copyIconButton} ${nameCopied ? classes.copyIconButtonSuccess : ''}`}
						>
							{nameCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
						</IconButton>
					</Tooltip>
				</div>
				<div className={classes.viewValue}>
					{viewingTemplate?.email_template_name || '-'}
				</div>
			</div>

			<div className={classes.viewSection}>
				<div className={classes.viewHeader}>
					<Typography className={classes.viewLabel}>HTML Preview</Typography>
					<Tooltip title={viewHtmlCopied ? "Copied!" : "Copy HTML"} arrow>
						<IconButton
							size="small"
							onClick={copyViewHtml}
							className={`${classes.copyIconButton} ${viewHtmlCopied ? classes.copyIconButtonSuccess : ''}`}
						>
							{viewHtmlCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
						</IconButton>
					</Tooltip>
				</div>
				{viewingTemplate?.email_template_html ? (
					<div
						className={classes.htmlPreview}
						dangerouslySetInnerHTML={{
							__html: highlightParams(
								viewingTemplate?.email_template_html
									?.replace(/<!DOCTYPE html>[\s\S]*<body>/i, '')
									.replace(/<\/body>[\s\S]*<\/html>/i, '') || '-'
							)
						}}
						onClick={(e) => {
							if (e.target.tagName === 'A' && e.target.href) {
								e.preventDefault();
								window.open(e.target.href, '_blank', 'noopener,noreferrer');
							}
						}}
					/>
				) : (
					<div className={classes.viewValue}>-</div>
				)}
			</div>

			<div className={classes.viewSection}>
				<Typography className={classes.viewLabel}>Created Date</Typography>
				<div className={classes.viewValue}>
					{viewingTemplate?.dt ? moment(viewingTemplate.dt).format(globalDateFormat) : '-'}
				</div>
			</div>

			<div className={classes.viewSection}>
				<Typography className={classes.viewLabel}>Updated Date</Typography>
				<div className={classes.viewValue}>
					{viewingTemplate?.dtu ? moment(viewingTemplate.dtu).format(globalDateFormat) : '-'}
				</div>
			</div>
		</div>
	);

	// CSV filename
	const csvFilename = `email_templates_${dateRange.startDate.format('YYYY-MM-DD')}_to_${dateRange.endDate.format('YYYY-MM-DD')}_${moment().format('YYYY-MM-DD')}.csv`;

	return (
		<>
			{/* Header Row */}
			<div className={classes.headerRow}>
				<div className={classes.leftControls}>

				</div>
				<div className={classes.rightControls}>
					{/* Refresh button */}
					<Tooltip title="Refresh" arrow>
						<IconButton
							className={classes.refreshButton}
							onClick={handleRefresh}
							disabled={loading}
						>
							<SyncIcon />
						</IconButton>
					</Tooltip>
					<ButtonOmni
						onClick={handleCreate}
						label={
							<Box display="flex" alignItems="center" gap={1}>
								<Icon style={{ fontSize: 20 }}>person_add</Icon>
								<Typography style={{ marginLeft: '8px' }}>
									Create Template
								</Typography>
							</Box>
						}
						size="small"
					/>
				</div>
			</div>

			{/* Table */}
			<div className={classes.tableSection}>
				<TableOmniNewAnimated
					onEditClick={handleEditClick}
					onDeleteClick={handleDeleteClick}
					onViewClick={handleViewClick}
					headCells={headCells}
					rows={data}
					loading={loading}
					totalItems={totalItems}
					page={currentParams.page}
					rowsPerPage={currentParams.limit}
					onPageChange={(e, newPage) => handlePaginationChange(newPage, currentParams.limit)}
					onRowsPerPageChange={(e) => handlePaginationChange(0, parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50, 100]}
					perPageRows={currentParams.limit}
					curPage={currentParams.page}
					setCurrentParams={setCurrentParams}
					emptyMessage="No Templates Found"
				/>
			</div>

			{/* Create Modal */}
			<ModalOmni
				open={createModalOpen}
				handleClose={() => setCreateModalOpen(false)}
				title="Create Email Template"
				content={renderFormContent()}
				customWidth={700}
				disableBackdropClick={true}
				disableEscapeKeyDown={true}
				actions={
					<>
						<ButtonOmni
							onClick={() => setCreateModalOpen(false)}
							variant="outlined"
							label="Cancel"
							size="small"
							disabled={isCreating}
						/>
						<ButtonOmni
							onClick={handleCreateConfirm}
							label={isCreating ? 'Creating...' : 'Create'}
							size="small"
							loading={isCreating}
							disabled={isCreating || !formData.name.trim() || !!nameError}
						/>
					</>
				}
			/>

			{/* Edit Modal */}
			<ModalOmni
				open={editModalOpen}
				handleClose={() => {
					setEditModalOpen(false);
					setEditingTemplate(null);
				}}
				title="Edit Email Template"
				content={renderFormContent()}
				customWidth={700}
				disableBackdropClick={true}
				disableEscapeKeyDown={true}
				actions={
					<>
						<ButtonOmni
							onClick={() => {
								setEditModalOpen(false);
								setEditingTemplate(null);
							}}
							variant="outlined"
							label="Cancel"
							size="small"
							disabled={isUpdating}
						/>
						<ButtonOmni
							onClick={handleEditConfirm}
							label={isUpdating ? 'Updating...' : 'Update'}
							size="small"
							loading={isUpdating}
							disabled={isUpdating || !formData.name.trim() || !!nameError}
						/>
					</>
				}
			/>

			{/* View Modal - Updated to match ScheduleTab size */}
			<ModalOmni
				open={viewModalOpen}
				handleClose={handleCloseViewDialog}
				title="Template Details"
				content={renderViewContent()}
				customWidth={700}
				maxWidth="md"
				disableBackdropClick={true}
				disableEscapeKeyDown={true}
				actions={
					<ButtonOmni
						onClick={handleCloseViewDialog}
						variant="outlined"
						label="Close"
						size="small"
					/>
				}
			/>

			{/* Delete Confirmation Dialog */}
			<DeleteConfirmationDialog
				open={deleteDialogOpen}
				onClose={() => {
					setDeleteDialogOpen(false);
					setDeletingTemplate(null);
				}}
				onConfirm={handleDeleteConfirm}
				title="Delete Template"
				message={`Are you sure you want to delete "${deletingTemplate?.email_template_name}"? This action cannot be undone.`}
				confirmLabel="Delete"
				cancelLabel="Cancel"
				loading={isDeleting}
			/>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
}

export default TemplatesTab;